import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SearchIcon as PlainSearchIcon } from '@heroicons/react/outline';
import { isDefined } from '@showroomjs/core';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import * as React from 'react';
import { SpinIcon } from './icons';
import { spinAnimation } from './animations';
import { useStableCallback } from '../lib/use-stable-callback';
import { css, styled } from '../stitches.config';
import { Dialog } from './dialog';
import { ShortcutKey } from './shortcut-key';
import { TextHighlight } from './text-highlight';
import { TextInput } from './text-input';
const SearchDialogImpl = function SearchDialog(props) {
    return (_jsx(Dialog.Content, { "aria-label": "Search", showCloseBtn: false, css: { maxHeight: 'none' }, children: _jsx(SearchDialogInternal, { ...props }) }));
};
const SearchDialogInternal = function SearchDialog(props) {
    const [inputValue, setInputValue] = React.useState('');
    const trimmedInput = inputValue.trim();
    const onInputChange = useStableCallback(props.onInputChange);
    const { options } = props;
    React.useEffect(() => {
        onInputChange(trimmedInput);
    }, [trimmedInput]);
    const hasSearchTerm = trimmedInput !== '';
    const dismissDialog = React.useContext(DismissContext);
    const { isOpen: isResultsShown, highlightedIndex, getItemProps, getInputProps, getComboboxProps, getMenuProps, } = useCombobox({
        inputValue,
        isOpen: hasSearchTerm,
        selectedItem: null,
        items: hasSearchTerm ? options : [],
        itemToString: (i) => (i ? i.label : ''),
        onSelectedItemChange: (changes) => {
            if (hasSearchTerm) {
                setInputValue('');
                dismissDialog();
                props.onSelect(changes.selectedItem ? changes.selectedItem.value : null, trimmedInput);
            }
            else {
                if (changes.selectedItem) {
                    setInputValue(changes.selectedItem.label);
                }
            }
        },
        onHighlightedIndexChange: (changes) => {
            if (isDefined(changes.highlightedIndex) &&
                changes.highlightedIndex > -1 &&
                props.onHighlightedItemChange) {
                const item = options[changes.highlightedIndex];
                if (item) {
                    props.onHighlightedItemChange(item.value);
                }
            }
        },
    });
    return (_jsxs(SearchMenuRoot, { ...getComboboxProps({
            className: props.className,
        }), children: [_jsxs(InputWrapper, { children: [_jsx(SearchIcon, { width: 24, height: 24 }), _jsx(TextInput, { onValue: setInputValue, css: {
                            paddingLeft: '$12',
                            py: '$3',
                        }, ...getInputProps({
                            placeholder: props.placeholder,
                        }) }), props.isLoading && (_jsx(LoadingWrapper, { children: _jsx(SpinIcon, { "aria-label": "Updating...", css: {
                                color: '$gray-500',
                                animation: `${spinAnimation} 1s linear infinite`,
                            } }) })), _jsx(Dialog.Close, { asChild: true, children: _jsx(ShortcutKey, { css: {
                                position: 'absolute',
                                right: '$3',
                                top: '$4',
                                display: 'none',
                                '@md': {
                                    display: 'block',
                                },
                            }, as: "button", children: _jsx("kbd", { children: _jsx(Abbr, { title: "Escape", children: "esc" }) }) }) })] }), isResultsShown && options.length === 0 && (_jsx(NoResult, { children: "No results found." })), _jsx(Menu, { ...getMenuProps(), children: isResultsShown &&
                    options.map((option, i) => {
                        const highlighted = i === highlightedIndex;
                        return (_jsx(Item, { children: _jsxs(OptionItem, { ...getItemProps({
                                    item: option,
                                    index: i,
                                }), highlighted: highlighted, children: [option.icon && _jsx(OptionIcon, { children: option.icon }), _jsxs(OptionText, { children: [_jsx("div", { children: _jsx(TextHighlight, { textToHighlight: option.label, searchWords: [trimmedInput], highlightClassName: highlighted ? underline().className : undefined }) }), option.description && (_jsx(Description, { children: _jsx(TextHighlight, { textToHighlight: option.description, searchWords: [trimmedInput], highlightClassName: highlighted ? underline().className : undefined }) }))] })] }) }, i));
                    }) })] }));
};
const SearchMenuRoot = styled('div', {
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
});
const Menu = styled('ul', {
    flex: 1,
    margin: '0',
    padding: '0',
    listStyle: 'none',
    maxHeight: '70vh',
    overflowY: 'auto',
});
const Item = styled('li', {
    margin: 0,
    padding: 0,
    width: '100%',
});
const DismissContext = React.createContext(() => {
    /* noop */
});
const SearchDialogRoot = ({ children }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const dismisss = React.useCallback(() => setIsOpen(false), []);
    React.useEffect(() => {
        const onKeyDown = (event) => {
            if (
            // The `Cmd+K` shortcut both opens and closes the modal.
            event.key === 'k' &&
                (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setIsOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, []);
    return (_jsx(DismissContext.Provider, { value: dismisss, children: _jsx(Dialog, { open: isOpen, onOpenChange: setIsOpen, children: children }) }));
};
export const SearchDialog = Object.assign(SearchDialogImpl, {
    Trigger: function SearchDialogTrigger({ children, className, ...props }) {
        return (_jsx(Dialog.Trigger, { asChild: true, children: _jsxs(TextInput, { as: "button", className: cx(triggerInput({}), className), css: {
                    color: '$gray-400',
                    height: 38,
                    width: 38,
                    borderRadius: '$full',
                    px: '$1',
                    justifyContent: 'center',
                    '@md': {
                        width: 'auto',
                        borderRadius: '$base',
                        px: '$3',
                        justifyContent: 'start',
                    },
                }, ...props, children: [_jsx(PlainSearchIcon, { width: 16, height: 16 }), children, _jsxs(ShortcutKey, { css: {
                            display: 'none',
                            '@md': {
                                display: 'block',
                            },
                        }, children: [_jsx(Kbd, { children: _jsx(Abbr, { title: "Command", children: "\u2318" }) }), _jsx(Kbd, { children: "K" })] })] }) }));
    },
    Root: SearchDialogRoot,
});
const InputWrapper = styled('div', {
    position: 'relative',
    backgroundColor: 'White',
});
const SearchIcon = styled(PlainSearchIcon, {
    position: 'absolute',
    left: '$3',
    top: 14,
    color: '$primary-300',
    '@md': {
        top: '$3',
    },
});
const OptionIcon = styled('div', {
    flexShrink: 0,
    color: '$gray-400',
});
const OptionText = styled('div', {
    flex: 1,
    minWidth: 0,
});
const Kbd = styled('kbd', {
    fontFamily: '$sans',
});
const Description = styled('div', {
    color: '$gray-500',
    fontSize: '$sm',
    lineHeight: '$sm',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
});
const OptionItem = styled('div', {
    display: 'flex',
    alignItems: 'center',
    gap: '$2',
    px: '$3',
    py: '$2',
    color: 'Black',
    cursor: 'pointer',
    variants: {
        highlighted: {
            true: {
                backgroundColor: '$primary-700',
                color: 'White',
                [`& ${Description}`]: {
                    color: '$gray-200',
                },
                [`& ${OptionIcon}`]: {
                    color: '$gray-200',
                },
            },
        },
    },
});
const NoResult = styled('div', {
    color: '$gray-600',
    py: '$6',
    textAlign: 'center',
});
const Abbr = styled('abbr', {
    textDecoration: 'none',
});
const triggerInput = css({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '$2',
    '&:focus-visible svg': {
        color: '$primary-400',
    },
    [`&:focus-visible ${Kbd}`]: {
        color: '$primary-400',
    },
});
const LoadingWrapper = styled('div', {
    position: 'absolute',
    top: 13,
    right: '$3',
    '@md': {
        right: '$14',
    },
});
const underline = css({
    textDecoration: 'underline',
    backgroundColor: 'inherit',
    color: 'inherit',
});
//# sourceMappingURL=search-dialog.js.map